// 📁 src/pages/PreparedTransfersPage.jsx
import { useEffect, useState } from "react";
import { Table } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";
import { Eye, Printer, Send, RotateCcw, Plus } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Načte Calibri regular + bold z public/fonts/ a zaregistruje je do jsPDF.
 */
async function ensureCalibriLoaded(doc) {
  if (window.__calibri_cache__) {
    const { regularBase64, boldBase64 } = window.__calibri_cache__;
    doc.addFileToVFS("calibri.ttf", regularBase64);
    doc.addFont("calibri.ttf", "Calibri", "normal");
    doc.addFileToVFS("calibri_bold.ttf", boldBase64);
    doc.addFont("calibri_bold.ttf", "Calibri", "bold");
    return;
  }
  async function loadFont(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Nepovedlo se načíst ${url}`);
    const buf = await res.arrayBuffer();
    const uint8 = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
    return btoa(binary);
  }
  const regularBase64 = await loadFont("/fonts/calibri.ttf");
  const boldBase64 = await loadFont("/fonts/calibri_bold.ttf");
  window.__calibri_cache__ = { regularBase64, boldBase64 };
  doc.addFileToVFS("calibri.ttf", regularBase64);
  doc.addFont("calibri.ttf", "Calibri", "normal");
  doc.addFileToVFS("calibri_bold.ttf", boldBase64);
  doc.addFont("calibri_bold.ttf", "Calibri", "bold");
}

export default function PreparedTransfersPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [detailPharmacy, setDetailPharmacy] = useState(null);
  const [pharmacies, setPharmacies] = useState({}); // { code: { fullName, phone, address, carrierCustomerNumber } }

  // 🔵 Nové pro diff/reopen:
  const [removedModalOpen, setRemovedModalOpen] = useState(false);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [removedList, setRemovedList] = useState([]); // poslední diff.removed

  // ========== Načtení přihlášeného uživatele ==========
  const getUser = async () => {
    try {
      const res = await fetch("/api/protected/me", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const json = await res.json();
      return {
        pharmacyCode: json?.user?.pharmacyCode?.replace(/^0+/, ""),
        firma: json?.user?.firma,
      };
    } catch (e) {
      console.error("Chyba při načítání uživatele:", e);
      return null;
    }
  };

  // ========== Načtení všech poboček ==========
  const fetchPharmacies = async () => {
    try {
      const res = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
    const json = await res.json();
      const map = {};
      (json.users || []).forEach((u) => {
        if (u.pharmacyCode)
          map[u.pharmacyCode.replace(/^0+/, "")] = {
            fullName: u.fullName || "",
            firma: u.firma || "",
            phone: u.phone || "",
            address: u.address || {},
            carrierCustomerNumber: u.carrierCustomerNumber || "",
          };
      });
      setPharmacies(map);
    } catch (e) {
      console.error("Nepodařilo se načíst seznam poboček:", e);
    }
  };

  // ========== Načtení dat připravených převodů ==========
  const fetchData = async (firma, pharmacyCode) => {
    try {
      const res = await fetch(`/api/transfers/prepared/${firma}/${pharmacyCode}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error("Chyba načítání prepared transfers:", e);
    } finally {
      setLoading(false);
    }
  };

  // ========== Načtení diffu – položky odstraněné mezi posledními dvěma uploady ==========
  const fetchRemoved = async () => {
    if (!user) return;
    setRemovedLoading(true);
    try {
      const res = await fetch(
        `/api/pool/removedSinceLastUpload/${user.firma}/${user.pharmacyCode}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const json = await res.json();
      const removed = json?.diff?.removed || [];
      setRemovedList(removed);
    } catch (e) {
      console.error("Chyba načtení odstraněných položek:", e);
    } finally {
      setRemovedLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const u = await getUser();
      setUser(u);
      await fetchPharmacies();
      if (u) fetchData(u.firma, u.pharmacyCode);
    })();
  }, []);

  if (loading) return <p>Načítám…</p>;

  // ========== Seskupení dat podle cílové pobočky ==========
  const grouped = data.reduce((acc, item) => {
    const key = item.targetPharmacyCode;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // ========== Generování PDF ==========
  const generatePDF = async (targetPharmacy, rows) => {
    const doc = new jsPDF("p", "mm", "a4");
    await ensureCalibriLoaded(doc);
    doc.setFont("Calibri", "normal");

    const today = new Date().toLocaleDateString("cs-CZ");
    const fromCode = user?.pharmacyCode || "-";
    const toCode = targetPharmacy;

    const fromInfo = pharmacies[fromCode] || {};
    const toInfo = pharmacies[toCode] || {};

    const fromName = fromInfo.fullName ? ` (${fromInfo.fullName})` : "";
    const toName = toInfo.fullName ? ` (${toInfo.fullName})` : "";

    // ---------- Stránka 1: Převodka ----------
    doc.setFontSize(12);
    doc.text("Převodka", 14, 15);
    doc.setFontSize(9);
    doc.text(`Datum: ${today}`, 14, 22);
    doc.text(`Z pobočky: ${fromCode}${fromName}`, 14, 28);
    doc.text(`Do pobočky: ${toCode}${toName}`, 14, 34);

    autoTable(doc, {
      startY: 40,
      head: [["PDK", "Název", "Množství", "Expirace", "Šarže"]],
      body: rows.map((row) => [
        row.PDK ?? "",
        row.Název ?? "",
        row.Množství ?? "",
        row.Exspirace ? new Date(row.Exspirace).toLocaleDateString("cs-CZ") : "",
        row.Šarže ?? "",
      ]),
      styles: {
        font: "Calibri",
        fontSize: 8,
        cellPadding: 1,
        lineColor: [180, 180, 180],
        lineWidth: 0.1,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: {
        font: "Calibri",
        fontSize: 8,
        fillColor: [240, 240, 240],
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: { halign: "left" },
      theme: "grid",
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 85 },
        2: { cellWidth: 20, halign: "center" },
        3: { cellWidth: 28, halign: "center" },
        4: { cellWidth: 28 },
      },
      margin: { top: 10, right: 12, bottom: 10, left: 12 },
    });

    // ---------- Stránka 2: Průvodka pro přepravce ----------
    doc.addPage("a4", "landscape");
    doc.setFont("Calibri", "normal");
    doc.setFontSize(14);

    let yO = 40;
    doc.setFont("Calibri", "bold");
    doc.text("Odesílatel:", 25, yO);
    doc.setFont("Calibri", "normal");
    yO += 7;
    doc.text("Lékárna LEMON", 25, yO);
    yO += 6;
    doc.text(fromInfo.address?.street || "", 25, yO);
    yO += 6;
    const cityZip2 = [fromInfo.address?.city, fromInfo.address?.zip].filter(Boolean).join(" ");
    doc.text(cityZip2, 25, yO);
    yO += 6;
    if (fromInfo.carrierCustomerNumber)
      doc.text(`Zákaznické číslo: ${fromInfo.carrierCustomerNumber}`, 25, yO);

    let yA = 150;
    doc.setFont("Calibri", "bold");
    doc.text("Příjemce:", 270, yA, { align: "right" });
    doc.setFont("Calibri", "normal");
    yA += 7;
    doc.text("Lékárna LEMON", 270, yA, { align: "right" });
    yA += 6;
    doc.text(toInfo.address?.street || "", 270, yA, { align: "right" });
    yA += 6;
    const cityZip = [toInfo.address?.city, toInfo.address?.zip].filter(Boolean).join(" ");
    doc.text(cityZip, 270, yA, { align: "right" });
    yA += 6;
    if (toInfo.carrierCustomerNumber)
      doc.text(`Zákaznické číslo: ${toInfo.carrierCustomerNumber}`, 270, yA, { align: "right" });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  };

  // ========== Odeslání převodu ==========
  const sendTransfer = async (targetPharmacy) => {
    if (!user) return;
    if (!window.confirm(`Opravdu odeslat balík pro pobočku ${targetPharmacy}?`)) return;
    try {
      await fetch(`/api/transfers/send/${user.firma}/${user.pharmacyCode}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ targetPharmacyCode: targetPharmacy }),
      });
      fetchData(user.firma, user.pharmacyCode);
      if (detailPharmacy === targetPharmacy) setDetailPharmacy(null);
    } catch (e) {
      console.error("Chyba při odesílání:", e);
    }
  };

  // ========== Modal – Odstraněné položky mezi posledními dvěma exporty ==========
  const openRemovedModal = async () => {
    setRemovedModalOpen(true);
    await fetchRemoved();
  };

  const reopenItem = async (item) => {
    if (!user) return;
    try {
      const res = await fetch(`/api/pool/reopen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          firma: user.firma,
          pharmacyCode: user.pharmacyCode,
          Název: item?.Název,
          Šarže: item?.Šarže || "",
          Exspirace: item?.Exspirace,
        }),
      });
      const json = await res.json();
      if (!json?.ok) {
        alert(json?.error || "Reopen selhalo.");
        return;
      }
      await fetchData(user.firma, user.pharmacyCode);
      await fetchRemoved();
    } catch (e) {
      console.error("Chyba reopen:", e);
      alert("Reopen selhalo.");
    }
  };

  // ========== UI render ==========
  return (
    <div className="page">
      <h1>Převodky – Vychystané</h1>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <div />
        <button
          onClick={openRemovedModal}
          style={{
            padding: "6px 10px",
            background: "#ffc107",
            color: "black",
            border: "none",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
          title="Zobrazí položky, které zmizely mezi posledními dvěma exporty – můžete je ručně znovu otevřít a odeslat."
        >
          <RotateCcw size={16} />
          Odstraněné při posledním exportu
        </button>
      </div>

      <Table className="styled-table">
        <thead>
          <tr>
            <th>Cílová pobočka</th>
            <th>Počet položek</th>
            <th>Akce</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(grouped).map((pharmacy) => (
            <tr key={pharmacy}>
              <td>
                {pharmacy}
                {pharmacies[pharmacy]?.fullName ? ` (${pharmacies[pharmacy].fullName})` : ""}
              </td>
              <td>{grouped[pharmacy].length}</td>
              <td style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setDetailPharmacy(pharmacy)}
                  style={{
                    padding: "4px 8px",
                    background: "#6c757d",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <Eye size={16} style={{ marginRight: "4px" }} />
                  Zobrazit
                </button>
                <button
                  onClick={() => generatePDF(pharmacy, grouped[pharmacy])}
                  style={{
                    padding: "4px 8px",
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <Printer size={16} style={{ marginRight: "4px" }} />
                  Vytisknout
                </button>
                <button
                  onClick={() => sendTransfer(pharmacy)}
                  style={{
                    padding: "4px 8px",
                    background: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <Send size={16} style={{ marginRight: "4px" }} />
                  Odeslat
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal detailu */}
      {detailPharmacy && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "100px",
            zIndex: 1000,
          }}
          onClick={() => setDetailPharmacy(null)}
        >
          <div
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "8px",
              width: "80%",
              maxHeight: "80%",
              overflowY: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>
              Pobočka {detailPharmacy}
              {pharmacies[detailPharmacy]?.fullName ? ` (${pharmacies[detailPharmacy].fullName})` : ""}
            </h2>
            <Table className="styled-table">
              <thead>
                <tr>
                  <th>PDK</th>
                  <th style={{ width: "25%" }}>Název</th>
                  <th>Expirace</th>
                  <th>Šarže</th>
                  <th>Množství</th>
                </tr>
              </thead>
              <tbody>
                {grouped[detailPharmacy].map((row) => (
                  <tr key={row._id}>
                    <td>{row.PDK}</td>
                    <td>{row.Název}</td>
                    <td>{row.Exspirace ? new Date(row.Exspirace).toLocaleDateString() : ""}</td>
                    <td>{row.Šarže}</td>
                    <td>{row.Množství}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 15,
              }}
            >
              <button
                onClick={() => generatePDF(detailPharmacy, grouped[detailPharmacy])}
                style={{
                  padding: "6px 12px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                <Printer size={16} style={{ marginRight: "6px" }} />
                Vytisknout
              </button>
              <button
                onClick={() => sendTransfer(detailPharmacy)}
                style={{
                  padding: "6px 12px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                <Send size={16} style={{ marginRight: "6px" }} />
                Odeslat
              </button>
              <button
                onClick={() => setDetailPharmacy(null)}
                style={{
                  padding: "6px 12px",
                  background: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal – Odstraněné položky */}
      {removedModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "80px",
            zIndex: 1100,
          }}
          onClick={() => setRemovedModalOpen(false)}
        >
          <div
            style={{
              background: "white",
              padding: 16,
              borderRadius: 8,
              width: "90%",
              maxHeight: "80%",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Odstraněné při posledním exportu</h2>
              <button
                onClick={() => setRemovedModalOpen(false)}
                style={{ padding: "6px 10px", border: "none", background: "#dc3545", color: "white", borderRadius: 6 }}
              >
                Zavřít
              </button>
            </div>

            {removedLoading ? (
              <p>Načítám…</p>
            ) : removedList.length === 0 ? (
              <p>Žádné položky nebyly odstraněny mezi posledními dvěma exporty.</p>
            ) : (
              <Table className="styled-table">
                <thead>
                  <tr>
                    <th style={{ width: 260 }}>Název</th>
                    <th>Exspirace</th>
                    <th>Šarže</th>
                    <th>Množství (min.)</th>
                    <th style={{ width: 140 }}>Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {removedList.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it?.Název || ""}</td>
                      <td>{it?.Exspirace ? new Date(it.Exspirace).toLocaleDateString("cs-CZ") : ""}</td>
                      <td>{it?.Šarže || ""}</td>
                      <td>{it?.Množství ?? ""}</td>
                      <td>
                        <button
                          onClick={() => reopenItem(it)}
                          style={{
                            padding: "4px 8px",
                            background: "#17a2b8",
                            color: "white",
                            border: "none",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                          title="Znovu otevřít (nastaví stav 'odesláno' se zachováním původních dat)"
                        >
                          <Plus size={16} />
                          Znovu otevřít
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
